import { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  IconButton,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Text,
  useToast,
} from "@chakra-ui/react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { crm, Pill } from "../../../../theme";
import { AttrCondition, AttrDef, AttrValue, AttrValues, CatalogAttributeSet } from "../../../../models/CatalogAttributes";
import {
  getCatalogAttributes,
  getCatalogProductValues,
  saveCatalogProductValues,
} from "../../../../services/catalogAttributesService";
import { formatNcm } from "../../../process/components/tableStyles";

const labelProps = { fontSize: "12px", fontWeight: "600", color: crm.textMuted, mb: "4px" };
const inputProps = { h: "42px", borderRadius: "10px", focusBorderColor: crm.brand };

/* ---- regras (espelham CatalogAttributeRules no backend) ---- */

const valuesOf = (v: AttrValue | undefined): string[] => {
  if (v == null) return [];
  const arr = Array.isArray(v) ? v : [v];
  return arr.filter((x): x is string => typeof x === "string" || typeof x === "number").map((x) => String(x).trim()).filter(Boolean);
};

const num = (s: string) => {
  const n = Number(s.replace(",", "."));
  return Number.isNaN(n) ? null : n;
};

const cmp = (op: string, actual: string, expected: string) => {
  if ([">", "<", ">=", "<="].includes(op)) {
    const a = num(actual);
    const b = num(expected);
    if (a != null && b != null) return op === ">" ? a > b : op === "<" ? a < b : op === ">=" ? a >= b : a <= b;
  }
  const eq = actual.trim().toLowerCase() === expected.trim().toLowerCase();
  return op === "!=" || op === "<>" ? !eq : eq;
};

export const conditionMatches = (c: AttrCondition | null | undefined, parent: string[]): boolean => {
  if (!c) return false;
  const cur = parent.some((v) => cmp(c.operator, v, c.value));
  if (!c.next) return cur;
  const next = conditionMatches(c.next, parent);
  return c.composition === "&&" ? cur && next : cur || next;
};

type Visible = { def: AttrDef; required: boolean; multi: boolean };

export const visibleAttributes = (set: CatalogAttributeSet, values: AttrValues): Visible[] => {
  const out: Visible[] = [];
  const walk = (d: AttrDef, required: boolean, multi: boolean) => {
    out.push({ def: d, required, multi: multi || d.multi });
    const pv = valuesOf(values[d.code]);
    d.conditioned.forEach((c) => {
      if (conditionMatches(c.condition, pv)) walk(c.attribute, c.required, c.multi);
    });
  };
  set.attributes.forEach((a) => walk(a, a.required, a.multi));
  return out;
};

const objs = (v: AttrValue | undefined): Record<string, string>[] =>
  Array.isArray(v) ? ((v as unknown[]).filter((x) => typeof x === "object" && x) as Record<string, string>[]) : v && typeof v === "object" ? [v as Record<string, string>] : [];

export const isFilled = (d: AttrDef, values: AttrValues): boolean => {
  if (!d.isComposite) return valuesOf(values[d.code]).length > 0;
  const list = objs(values[d.code]);
  return (
    list.length > 0 &&
    list.every(
      (o) => d.sub.filter((s) => s.required).every((s) => valuesOf(o[s.code]).length > 0) && d.sub.some((s) => valuesOf(o[s.code]).length > 0)
    )
  );
};

export const progress = (set: CatalogAttributeSet, values: AttrValues) => {
  const req = visibleAttributes(set, values).filter((v) => v.required);
  return { required: req.length, filled: req.filter((v) => isFilled(v.def, values)).length };
};

/* ---- campos ---- */

const SimpleField = ({
  def,
  multi,
  value,
  onChange,
}: {
  def: AttrDef;
  multi: boolean;
  value: AttrValue | undefined;
  onChange: (v: AttrValue | undefined) => void;
}) => {
  const cur = valuesOf(value);
  const single = cur[0] ?? "";
  const set = (v: string) => onChange(v === "" ? undefined : v);

  if (def.domain.length > 0) {
    if (multi)
      return (
        <Flex wrap="wrap" gap="8px 16px">
          {def.domain.map((o) => (
            <Checkbox
              key={o.code}
              isChecked={cur.includes(o.code)}
              onChange={(e) => {
                const next = e.target.checked ? [...cur, o.code] : cur.filter((x) => x !== o.code);
                onChange(next.length ? next : undefined);
              }}
            >
              <Text fontSize="13px">{o.description || o.code}</Text>
            </Checkbox>
          ))}
        </Flex>
      );
    return (
      <Select {...inputProps} placeholder="Não informar" value={single} onChange={(e) => set(e.target.value)}>
        {def.domain.map((o) => (
          <option key={o.code} value={o.code}>
            {o.description || o.code}
          </option>
        ))}
      </Select>
    );
  }
  if (def.form === "BOOLEANO")
    return (
      <Select {...inputProps} placeholder="Não informar" value={single} onChange={(e) => set(e.target.value)}>
        <option value="true">Sim</option>
        <option value="false">Não</option>
      </Select>
    );
  if (multi)
    return (
      <Input
        {...inputProps}
        value={cur.join("; ")}
        placeholder="Valores separados por ;"
        onChange={(e) => {
          const parts = e.target.value.split(";").map((x) => x.trim()).filter(Boolean);
          onChange(parts.length ? parts : undefined);
        }}
      />
    );
  if (def.form === "DATA") return <Input {...inputProps} type="date" value={single} onChange={(e) => set(e.target.value)} />;
  if (def.form === "DATA_HORA")
    return <Input {...inputProps} type="datetime-local" value={single} onChange={(e) => set(e.target.value)} />;
  const numeric = def.form === "NUMERO_INTEIRO" || def.form === "NUMERO_REAL";
  return (
    <Input
      {...inputProps}
      value={single}
      inputMode={numeric ? "decimal" : undefined}
      maxLength={Math.min(def.maxLength ?? 100, 100)}
      placeholder={def.mask ?? undefined}
      onChange={(e) => {
        let v = e.target.value;
        if (def.form === "NUMERO_INTEIRO") v = v.replace(/\D/g, "");
        else if (def.form === "NUMERO_REAL") v = v.replace(/[^\d.,]/g, "");
        set(v);
      }}
    />
  );
};

const Field = ({
  def,
  required,
  multi,
  values,
  setValue,
  depth,
}: {
  def: AttrDef;
  required: boolean;
  multi: boolean;
  values: AttrValues;
  setValue: (code: string, v: AttrValue | undefined) => void;
  depth: number;
}) => {
  const missing = required && !isFilled(def, values);
  return (
    <Box mb="14px" pl={depth ? "14px" : 0} borderLeft={depth ? `2px solid ${crm.border}` : undefined}>
      <Text {...labelProps}>
        <Pill>{def.code}</Pill> {def.label}
        {required && <Text as="span" color={crm.danger}> *</Text>}
      </Text>
      {def.hint && (
        <Text fontSize="12px" color={crm.textFaint} mb="4px">
          {def.hint}
        </Text>
      )}
      {def.isComposite ? (
        <CompositeField def={def} multi={multi} value={values[def.code]} onChange={(v) => setValue(def.code, v)} />
      ) : (
        <SimpleField def={def} multi={multi} value={values[def.code]} onChange={(v) => setValue(def.code, v)} />
      )}
      {!def.hasDetails && (
        <Text fontSize="12px" color={crm.textFaint} mt="2px">
          Detalhes do atributo indisponíveis; informe o valor conforme o Portal Único.
        </Text>
      )}
      {missing && (
        <Text fontSize="12px" color={crm.danger} mt="2px">
          Obrigatório
        </Text>
      )}
    </Box>
  );
};

const CompositeField = ({
  def,
  multi,
  value,
  onChange,
}: {
  def: AttrDef;
  multi: boolean;
  value: AttrValue | undefined;
  onChange: (v: AttrValue | undefined) => void;
}) => {
  const rows = objs(value);
  const list = rows.length ? rows : [{}];
  const emit = (next: Record<string, string>[]) => {
    const clean = next.map((o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== "")));
    if (clean.every((o) => Object.keys(o).length === 0)) return onChange(undefined);
    onChange(multi ? clean : clean[0]);
  };
  const setSub = (i: number, code: string, v: string | undefined) => {
    const next = list.map((o, idx) => (idx === i ? { ...o, [code]: v ?? "" } : o));
    emit(next);
  };
  return (
    <Box>
      {list.map((row, i) => (
        <Box key={i} border={`1px solid ${crm.border}`} borderRadius={crm.radius} p="12px" mb="8px">
          {def.sub.map((s) => (
            <Box key={s.code} mb="10px">
              <Text {...labelProps}>
                {s.label}
                {s.required && <Text as="span" color={crm.danger}> *</Text>}
              </Text>
              <SimpleField
                def={s}
                multi={false}
                value={row[s.code]}
                onChange={(v) => setSub(i, s.code, typeof v === "string" ? v : undefined)}
              />
            </Box>
          ))}
          {multi && list.length > 1 && (
            <IconButton
              aria-label="remover"
              icon={<FaTrash />}
              size="sm"
              variant="ghost"
              color={crm.textMuted}
              _hover={{ color: crm.danger, bg: crm.subtle }}
              onClick={() => emit(list.filter((_, idx) => idx !== i))}
            />
          )}
        </Box>
      ))}
      {multi && list.length < 5 && (
        <Button
          size="sm"
          variant="outline"
          leftIcon={<FaPlus />}
          color={crm.brand}
          borderColor={crm.border}
          _hover={{ bg: crm.brandSoft }}
          onClick={() => emit([...list, {}])}
        >
          Adicionar
        </Button>
      )}
    </Box>
  );
};

/**
 * Atributos do Catálogo de Produtos (Portal Único) por produto (NCM + descrição + CNPJ raiz do cliente).
 * Definições vêm do CADATRIBUTOS via backend (cache 7 dias); campos condicionados só aparecem quando o atributo
 * pai tem o valor exigido. Valores são salvos antes do registro e enviados ao criar o produto no Catálogo.
 */
const CatalogAttributesModal = ({
  isOpen,
  onClose,
  ncm,
  product,
  cnpj,
}: {
  isOpen: boolean;
  onClose: () => void;
  ncm: string;
  product: string;
  cnpj?: string;
}) => {
  const toast = useToast();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [set, setSet] = useState<CatalogAttributeSet>();
  const [values, setValues] = useState<AttrValues>({});

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    Promise.all([getCatalogAttributes(ncm), getCatalogProductValues(ncm, product, cnpj)])
      .then(([s, v]) => {
        if (cancelled) return;
        setSet(s);
        setValues(v);
      })
      .catch((e: any) => {
        if (cancelled) return;
        const msg = e?.response?.data?.error;
        setError(typeof msg === "string" ? msg : "Não foi possível consultar os atributos no Portal Único.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [isOpen, ncm, product, cnpj]);

  const visible = useMemo(() => (set ? visibleAttributes(set, values) : []), [set, values]);
  const stats = useMemo(() => (set ? progress(set, values) : { required: 0, filled: 0 }), [set, values]);

  const setValue = (code: string, v: AttrValue | undefined) =>
    setValues((cur) => {
      const next = { ...cur };
      if (v === undefined) delete next[code];
      else next[code] = v;
      return next;
    });

  const save = async () => {
    // só guarda valores de atributos visíveis (condicionados escondidos não vão ao Portal)
    const keep = new Set(visible.map((v) => v.def.code));
    const clean: AttrValues = {};
    Object.entries(values).forEach(([k, v]) => {
      if (keep.has(k)) clean[k] = v;
    });
    setSaving(true);
    try {
      await saveCatalogProductValues(ncm, product, cnpj, clean);
      toast({ title: "Atributos salvos", status: "success" });
      onClose();
    } catch (e: any) {
      const data = e?.response?.data;
      toast({
        title: "Não foi possível salvar os atributos",
        description: typeof data === "string" ? data : undefined,
        status: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // profundidade só para recuo visual dos condicionados
  const depthOf = useMemo(() => {
    const map: Record<string, number> = {};
    const walk = (d: AttrDef, depth: number) => {
      map[d.code] = depth;
      d.conditioned.forEach((c) => walk(c.attribute, depth + 1));
    };
    set?.attributes.forEach((a) => walk(a, 0));
    return map;
  }, [set]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="3xl" scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(2px)" />
      <ModalContent borderRadius="16px" boxShadow={crm.shadowLg}>
        <ModalHeader borderBottom={`1px solid ${crm.border}`}>
          <Text fontSize="18px" fontWeight="700" color={crm.text}>
            Atributos do Catálogo de Produtos
          </Text>
          <Text fontSize="13px" fontWeight="400" color={crm.textMuted}>
            {formatNcm(ncm.replace(/\D/g, ""))} · {product || "sem descrição"}
            {set && ` · ${stats.filled}/${stats.required} obrigatórios`}
          </Text>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody py="20px">
          {loading ? (
            <Flex justify="center" py="40px">
              <Spinner color={crm.brand} />
            </Flex>
          ) : error ? (
            <Text fontSize="13px" color={crm.danger}>
              {error}
            </Text>
          ) : visible.length === 0 ? (
            <Text fontSize="13px" color={crm.textFaint}>
              Este NCM não tem atributos de importação no Catálogo.
            </Text>
          ) : (
            <Box border={`1px solid ${crm.border}`} borderRadius={crm.radius} p="16px">
              {visible.map((v) => (
                <Field
                  key={v.def.code}
                  def={v.def}
                  required={v.required}
                  multi={v.multi}
                  values={values}
                  setValue={setValue}
                  depth={depthOf[v.def.code] ?? 0}
                />
              ))}
            </Box>
          )}
        </ModalBody>
        <ModalFooter borderTop={`1px solid ${crm.border}`} gap="8px">
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            bg={crm.brand}
            color="white"
            _hover={{ bg: crm.brandDark }}
            isLoading={saving}
            isDisabled={loading || !!error}
            onClick={save}
          >
            Salvar atributos
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default CatalogAttributesModal;
